import {
    ChangeDetectorRef,
    Component,
    DestroyRef,
    inject,
    OnDestroy,
    OnInit,
    QueryList,
    signal,
    ViewChild,
    ViewChildren
} from '@angular/core';
import {provideNativeDateAdapter} from '@angular/material/core';
import {CommonModule} from '@angular/common';
import {MatSelectModule} from '@angular/material/select';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {BehaviorSubject, catchError, combineLatest, debounceTime, lastValueFrom, Observable, of, switchMap} from 'rxjs';
import {MatSidenavModule} from '@angular/material/sidenav';
import {MatIcon} from '@angular/material/icon';
import {MatAutocompleteModule} from '@angular/material/autocomplete';
import {MatInputModule} from '@angular/material/input';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MatDialog} from '@angular/material/dialog';
import {SideBarDoubleComponent} from '../../../shared/components/side-bar-double/side-bar-double.component';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ActivatedRoute} from '@angular/router';
import {DataTableService} from '../../../core/services/data-table.service';
import {RegisterHeaderComponent} from '../register/register-header/register-header.component';


import {PageEvent} from '@angular/material/paginator';
import {ChartComponent} from '../../../shared/components/chart/chart.component';
import {EXTENDED_SETTINGS_COLUMN, TableComponent} from '../../../shared/components/table/table.component';
import {TableServiceService} from './table-service.service';
import {AnalyticService, ApiReportAnalyticRatingParams, GroupService, Period} from '../../apiModule1';
import {ITableColumn} from '../../../core/common/interfaces/table-column.interface';
import {TableCellDirective} from '../../../shared/components/table/table-cell.directive';
import {FilterIndicatorsBarComponent} from '../register/filter-indicators-bar/filter-indicators-bar.component';
import {Sort, SortDirection} from '@angular/material/sort';
import {CommunicationService} from '../../../core/services/communication.service';
import {excludeId} from '../index-entity/index-entity.component';
import {PasFiltersStoreService} from '../../../core/services/pas-store.service';
import {PasFilterType} from '../../../shared/components/side-bar-double/menu-data.service';
import {ToPdfDirective} from "../../../core/directives/to-pdf.directive";
import {map} from "rxjs/operators";
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {Legends2Component} from "./legends2/legends2.component";
import {ColorValueService} from "./color-value.service";
import {
    ReportOccupiedPlacesFilterDialogComponent
} from "./report-occupied-places-filter-dialog/report-occupied-places-filter-dialog.component";
import {PeriodColumnsService} from "./period-columns.service";
import {MatProgressBar} from "@angular/material/progress-bar";


export interface TableData {
    id: number;
    name: string;
    summary: number;

    [key: `${typeof CELL_NAME}${number}`]: {
        id: number;
        name: string;
        value: number;
    };
}

@Component({
    selector: 'report-occupied-places',
    standalone: true,
    providers: [
        provideNativeDateAdapter(),
        {provide: DataTableService, useClass: TableServiceService}
    ],
    imports: [
        MatSelectModule,
        MatSidenavModule,
        MatIcon,
        FormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatAutocompleteModule,
        ReactiveFormsModule,
        CommonModule,
        MatDatepickerModule,
        SideBarDoubleComponent,
        RegisterHeaderComponent,
        TableComponent,
        TableCellDirective,
        FilterIndicatorsBarComponent,
        ToPdfDirective,
        MatProgressSpinner,
        Legends2Component,
        MatProgressBar,

    ],
    templateUrl: './report-occupied-places.component.html',
    styleUrls: ['./report-occupied-places.component.scss', '../../../shared/components/table/table.component.scss', '../register/register.component.scss'],
})

export class ReportOccupiedPlacesComponent implements OnInit, OnDestroy {
    analyticsService = inject(AnalyticService);
    private communicationService = inject(CommunicationService);
    filterStore = inject(PasFiltersStoreService);
    colorService = inject(ColorValueService);

    public opened = true;
    mobileOpen = false;

    @ViewChild(TableComponent) table!: TableComponent<any>;
    @ViewChild(ToPdfDirective) toPdf?: ToPdfDirective;
    @ViewChildren(ChartComponent) charts!: QueryList<ChartComponent>;

    protected type: PasFilterType = 'report-occupied-places'

    list = new BehaviorSubject<void>(undefined);

    updateList = new BehaviorSubject<void>(undefined);

    readonly FILTER_ITEMS = ['number', 'organization', EXTENDED_SETTINGS_COLUMN]

    private readonly emptyPager: Pager = {
        length: 0,
        pageIndex: 1,
        pageSize: 10,
        previousPageIndex: 0
    };

    search$ = new BehaviorSubject<string>('');
    sort$ = new BehaviorSubject<Sort>({direction: '', active: ''});
    filters$ = new BehaviorSubject<ApiReportAnalyticRatingParams | null>(null);
    filtersFromDialog$ = new BehaviorSubject<ApiReportAnalyticRatingParams | null>(null);
    pager$ = new BehaviorSubject<PageEvent>(this.emptyPager);

    searchObservable$ = this.search$.asObservable();
    sortObservable$ = this.sort$.asObservable();
    filtersObservable$ = this.filters$.asObservable();
    pagerObservable$ = this.pager$.asObservable();

    public searchPhrase: string = '';
    public rawData: TableData[] | null = null;


    groupService = inject(GroupService);
    groupName: string = '';
    pdfReportStatus = signal<boolean>(false);
    importingExcel = signal<boolean>(false);


    constructor(
        private route: ActivatedRoute,
        private destroyRef: DestroyRef,
        private dataTableService: DataTableService,
        private dialog: MatDialog,
        private cdr: ChangeDetectorRef,
        public periodColumnsService: PeriodColumnsService
    )
    {
        this.route.data
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(({group}) => {
                this.dataTableService.setData(group);
                this.dataTableService.setColumns([])
                this.dataTableService.getCustomColumns()
            });
    }

    get tableData(): any[] | undefined {
        const res = this.dataTableService.getData() as unknown as { pager: unknown, values: any[] };
        return res?.values;
    }

    refreshData(): Observable<any> {

        return combineLatest([this.sortObservable$, this.filtersObservable$, this.pagerObservable$])
            .pipe(
                debounceTime(200),
                switchMap(([search]) => {
                        return this.analyticsService.apiRatingAnalyticRating(
                            {
                                ...this.filters$.value,
                                // SearchString: search
                            }
                        );
                    }
                ))
            .pipe(catchError(err => {
                console.error(err);
                this.communicationService.publish('ошибка запроса данных', {type: 'error'});
                return of(null);
            }));
    }

    // periods$ = toObservable(this.periodColumnsService.getPeriodsSignal())
    //     .pipe(takeUntilDestroyed(this.destroyRef));

    ngOnInit(): void {
        this.dataTableService.showCustomColumns = false;

        const filterValue = this.filterStore.get(this.type);
        if (!!filterValue) {
            const newValue = {
                ...filterValue,
                // Periods: periods.filter(v => v !== null) as { year: number; quarter: number | null; }[]
            }
            this.filtersFromDialog$.next(newValue)
            this.filters$.next(newValue);
            const periods = filterValue.Periods;
            periods && periods.length && periods.forEach((e: Period) => {
                this.periodColumnsService.addToSelectedPeriod(e)
            })
        }

        this.refreshData().subscribe((v: Rating) => {
            this.setData(v);
            const tableData: TableData[] = this.dataTableService.getData().values;
            // this._setTableData(
            //     replaceIdToOrder(columnSort('number', 'desc', tableData))
            // )
            this.rawData = this.dataTableService.getData().values

            //--- если нет выбранных периодов, то добавляем их ---
            const storedFilters = this.filterStore.get(this.type)
            if (!(storedFilters && storedFilters.Periods && storedFilters.Periods.length)) {
                if (v && v.values && v.values[0] && v.values[0].values) {
                    Object.keys(v.values[0].values).forEach((e: string) => this.periodColumnsService.addToSelectedPeriod(e))
                }
            }

            this.initialSorting()
            this.loading = false
        })

        this.filters$.subscribe(() => {
            this.loading = true
        })

        // })

        this.filters$.subscribe(async v => {
            //--- ищем название группы учреждения ---
            const groups = await lastValueFrom(this.groupService.apiGroupGet({}).pipe(map(v => v.values)));
            const group: Group = groups.find((g: Group) => g.id === v?.OrganizationGroupIds?.[0]);
            this.groupName = group?.shortName || 'не задано';

            // this.filterStore.set(this.type,{...this.filters$.value, Periods: this.periodColumnsService.getSelectedSignal()()})
        })

        this.search$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(v => {
            const currentTableData = this.dataTableService.getData()
            const newTableData = {...currentTableData, values: this.rawData?.filter((e: any) => e.organization.toLowerCase().includes(v.toLowerCase()))}
            this.setData(newTableData)
        })

    }

    ngOnDestroy() {
        window.URL.revokeObjectURL(this.url);
    }

    initialSorting() {
        //начальная сортировка
        const sorting = this.dataTableService.getSorting() //данные из стора
        if (!sorting || !sorting.active || sorting.active === EXTENDED_SETTINGS_COLUMN) {
            //находим 3-ю колонку
            const column = this.dataTableService.getColumns().find((v, i) => i === 2);
            if (column) {
                this.onSortedColumn({active: column.name, direction: 'asc'} as Sort)
            }
        } else {
            this.onSortedColumn(sorting)
        }
    }


    setData(v: Rating) {
        if (!v) return
        //--- устанавливаем табличные данные
        const tableData = this.setTableDate(v);
        const data = {pager: v.pager, values: tableData}
        this.dataTableService.setData(data);

        this.table?.setDirectlySelectedColumns() //перерисовываем колонки
        this.cdr.detectChanges(); //перерисовываем таблицу

    }


    setTableDate(table: Rating): Record<string, any>[] {
        const tableData = {pages: table.pager, values: analyticTableToData(table.values)}
        const columnsData: ITableColumn[] = analyticTableToColumns(tableData.values);
        console.log('columnsData', columnsData);
        this.dataTableService.setColumns(columnsData);

        //запись списка колонок на сервер
        this.dataTableService.setCustomColumns(columnsData.map(v => v.name))


        const res = replaceIdToOrder(tableData.values as { id: number }[]);
        // const res1 = cellSort(columnsData[2].name,'asc', res)

        //фильтруем по organization в зависимости от searchPhrase


        return res.filter((e: any)=>e?.['organization']?.toLowerCase().includes(this.searchPhrase.toLowerCase()));
    }


    onPageChange(pager: PageEvent): void {
        this.pager$.next(pager);
    }

    onSortedColumn($event: any): void {
        if ($event.active === EXTENDED_SETTINGS_COLUMN) return;

        const tableData: TableData[] = this.dataTableService.getData().values;

        const sortedData = replaceNumberBySort(
            cellSort($event.active, $event.direction, tableData), $event.direction
        )
        this.dataTableService.setData({pager: this.pager$.value, values: sortedData})

        // const reverseDirect = $event.direction === 'desc' ? 'asc' : ($event.direction === '' ? '' : 'desc');

        if ($event.direction === '' && this.rawData) {
            return this._setTableData(this.rawData)
        }
    }

    private _setTableData(data: TableData[]): void {
        this.dataTableService.setData({pager: {}, values: data});
    }

    openFilterDialog() {
        this.dialog.open(ReportOccupiedPlacesFilterDialogComponent, {
            width: '690px',
            height: '100vh',
            position: {
                right: '0px'
            },
            data: this.filterStore.get(this.type)
        }).afterClosed().subscribe((v: ApiReportAnalyticRatingParams) => {
            if (!v) return;
            this.filtersFromDialog$.next(v);
            const newFilters = this._prepareFilterToSend({...this.filters$.value, ...v})
            this.filters$.next(newFilters);
            this.filterStore.set(this.type, newFilters)
        })
    }

    protected _prepareFilterToSend<T extends object>(filterObj: T): ApiReportAnalyticRatingParams {
        let res = Object.entries(filterObj)
        res = res.filter(([_, value]) => value !== null && value !== undefined)
        res = res.map(([k, v]) => {
            return [k, excludeId(v)]
        })
        const result = Object.fromEntries(res);
        return result
    }

    url: string = '';

    excel() {
        this.importingExcel.set(true);
        const params: ApiReportAnalyticRatingParams = {
            OrganizationGroupIds: this.filters$.value?.OrganizationGroupIds,
            Periods: this.filters$.value?.Periods,
            OrgStatusIds: this.filters$.value?.OrgStatusIds,
            OrganizationIds: this.filters$.value?.OrganizationIds,
        }
        this.analyticsService.apiRatingAnalyticRatingFileGet(params, 'body', false, {httpHeaderAccept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'})
            .subscribe((v: Blob) => {
                this.importingExcel.set(false);
                this.url = window.URL.createObjectURL(v);
                const a = document.createElement('a');
                a.href = this.url;
                a.download = 'report.xlsx';
                a.click();
            })
    }

    onSearch(): void {
        this.search$.next(this.searchPhrase);
        // this.loading = true;
    }

    public resetFilters(): void {
        this.filters$.next({});
        this.filtersFromDialog$.next({});
        this.filterStore.set(this.type, {})
        this.periodColumnsService.setPeriods(this.periodColumnsService.getInitialPeriods())
        this.periodColumnsService.clearSelectedPeriod()
        this.periodColumnsService.getInitialPeriods().slice(0, 4).forEach((e: Period) => {
            this.periodColumnsService.addToSelectedPeriod(e)
        })
    }

    refresh(): void {
        window.location.reload();
    }

    async onExportPdf() {
        if (!this.toPdf) return;
        await this.toPdf.snapshot(() => {}, 'top-report')
    }

    protected readonly Math = Math;
    protected loading = false;

}

export const CELL_NAME = 'cell'


function analyticTableToData(table: RatingValue[]): { [key: string]: string | number | null }[] {
    if (!table || !table.length) {
        return []
    }

    const values = table.map((v: RatingValue) => ({...v, ...v.values, values: null}))
    return values
}


function analyticTableToColumns(value: unknown[]): ITableColumn[] {
    if (!value || !value.length) return [];
    const entries = Object.entries(value[0] as any);
    const res = [
        ...entries.map(([key]): ITableColumn => {
            let name = key;
            let title = key;
            let dynamic = true;
            if (name === 'organization') {
                title = 'Учреждение'
                dynamic = false
            }
            if (title === 'number') {
                title = '№'
                dynamic = false
            }
            return {
                name,
                title,
                default: true,
                useCustomTemplate: !(key === 'number' || key === 'organization'),
                maxWidth: key === 'name' ? '25vw' : undefined,
                dynamic,
            }
        })
            .filter((v: ITableColumn) => v.title !== 'values')
    ]

    //сортировка
    const resSorted = res.sort((a: ITableColumn, b: ITableColumn) => {
            const aKey = a.name;
            const bKey = b.name;

            const aParsed = convertBackRatingAnalyticPeriod(aKey);
            const bParsed = convertBackRatingAnalyticPeriod(bKey);

            // Если не распарсилось — считаем, что это не дата, ставим в конец
            if (!aParsed || aParsed.year === null) return -1;
            if (!bParsed || bParsed.year === null) return -1;

            const aQuarter = aParsed.year * 4 + (aParsed.quarter ?? 4.5);
            const bQuarter = bParsed.year * 4 + (bParsed.quarter ?? 4.5);
            return bQuarter - aQuarter
        })
    ;

    //выносим необходимые колонки влево
    const organizationIndex = resSorted.findIndex(v => v.name === 'organization');
    if (organizationIndex !== -1) {
        resSorted.unshift(resSorted.splice(organizationIndex, 1,)[0])
    }
    const numberIndex = resSorted.findIndex(v => v.name === 'number');
    if (numberIndex !== -1) {
        resSorted.unshift(resSorted.splice(numberIndex, 1,)[0])
    }

    console.log('resSorted', resSorted)
    return resSorted;
}

export function delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
}

export function replaceIdToOrder<T extends { id: number }>(v: T[], direction: 'asc' | 'desc' = 'asc'): T[] {

    const fn = (direction: 'asc' | 'desc', val: number): number => {
        if (direction === 'asc') {
            return val;
        } else {
            return v.length - val + 1
        }
    }

    return v.map((e: T, idx: number) => ({...e, number: fn(direction, idx + 1)}));
}

export function cellSort(active: string, direction: SortDirection, tableData: TableData[]): TableData[] {
    if (direction === '') return tableData

    const fn = direction === 'asc'
        ? (a: TableData, b: TableData) => {
            if ((a as Record<string, any>)[active] === null && (b as Record<string, any>)[active] === null) return 0
            if ((a as Record<string, any>)[active] === null) return 1
            if ((b as Record<string, any>)[active] === null) return -1

            return (a as Record<string, any>)[active] - (b as Record<string, any>)[active]
        }
        : (b: TableData, a: TableData) => {
            if ((a as Record<string, any>)[active] === null && (b as Record<string, any>)[active] === null) return 0
            if ((a as Record<string, any>)[active] === null) return -1
            if ((b as Record<string, any>)[active] === null) return 1
            return (a as Record<string, any>)[active] - (b as Record<string, any>)[active]
        };
    const data = tableData.toSorted(fn)
    return data
}

export function columnSort(active: string, direction: SortDirection, tableData: TableData[]): TableData[] {
    if (direction === '') return tableData

    const fn: (a: TableData, b: TableData) => 1 | -1 | 0 = direction === 'asc'
        ? (a: TableData, b: TableData) => (a as Record<string, any>)[active] > (b as Record<string, any>)[active] ? 1 : -1
        : (a: TableData, b: TableData) => (a as Record<string, any>)[active] <= (b as Record<string, any>)[active] ? 1 : -1;
    const data = tableData.toSorted(fn)
    return data
}

export function convertRatingAnalyticPeriod(v: { year: number, quarter: number | null }[]): string[] {
    const res = v.map(({year, quarter}) => {
        if (quarter) {
            return `${quarter} кв. ${year}`
        } else {
            return `${year}`
        }
    })
    return res
}

export function convertBackRatingAnalyticPeriod(v: string): { year: number | null, quarter: number | null } | null {
    if (!v) return null
    if (v.includes('кв.')) {
        const [quarter, year] = v.split('кв.').map(v => v.trim());
        const dQuarter = Number.isNaN(parseInt(quarter)) ? null : parseInt(quarter);
        const dYear = Number.isNaN(parseInt(year)) ? null : parseInt(year);
        return {year: dYear, quarter: dQuarter}
    } else {
        const dV = Number.isNaN(parseInt(v)) ? null : parseInt(v);
        return {year: dV, quarter: null}
    }
}

function replaceNumberBySort(v: TableData[], direction: SortDirection = 'asc'): TableData[] {
    if (!v || direction === '') return v
    if (direction === 'asc') {
        return v.map((v, idx) => ({...v, number: idx + 1}))
    } else {
        const len = v.length
        return v.map((v, idx) => ({...v, number: len - idx}))
    }

}
